import { defineStore } from 'pinia'
import { ref } from 'vue'
import type { IApiProductSettings, IPlacements } from '@/types'
import {
  ensureActiveFilterGroups,
  ensureActiveFilters,
  ensureActiveVehicles,
  ensureAvailableFilterGroups,
  ensureCallToAction,
  ensureCardSettings,
  ensureLayoutSettings,
  ensureSliderArrowSettings,
  ensureSortBy,
  ensureSortDirection,
  ensureVehicleDetailsRows,
} from '@/stores/utils'
import { formatPlacementIdToFriendlyName } from '@/utils'
import { useJams, useJazelAppConfig } from '@jazel/jazel-vuetify'
import { useAccountSettingsStore } from '@/stores/accountSettings'

export const useProductSettingsStore = defineStore('productSettings', () => {
  const loading = ref(false)
  const error = ref<Error | null>(null)
  const initialized = ref(false)
  const productId = 'micro_srp'

  const appConfig = useJazelAppConfig()
  const accountSettings = useAccountSettingsStore()
  const jamsService = useJams()
  const placements = ref<IPlacements>({})
  const productSettings = ref<any | null>(null)

  const setProductSettings = (settings: any | null) => {
    // Use guaranteed working deep clone
    let mutatedSettings: any

    try {
      // Try structuredClone first
      mutatedSettings = settings ? structuredClone(settings) : {}
    } catch (error) {
      // Fallback to JSON method
      mutatedSettings = settings ? JSON.parse(JSON.stringify(settings)) : null
    }

    // Apply default values to all placements
    if (mutatedSettings == null) {
      mutatedSettings = {
        placements: {},
      }
    }

    if (!mutatedSettings.placements) {
      mutatedSettings.placements = {}
    }

    const normalizedPlacements = ensurePlacementDefaults(
      mutatedSettings.placements,
    )

    // Update the store with normalized settings
    updateSettings(normalizedPlacements)

    productSettings.value = { ...settings, placements: normalizedPlacements }
  }

  const getProductSettings = (): any => {
    return productSettings.value?.placements ?? {}
  }

  const updateSettings = (newSettings: IPlacements) => {
    placements.value = { ...newSettings }
  }

  const resetError = () => {
    error.value = null
  }

  const deletePlacement = async (placementId: string) => {
    // Fetch the current settings from JAMS
    const getParams = {
      accountId: accountSettings.accountId,
      endpoint: appConfig.jamsBackendUrl,
      isClientUser: accountSettings.isClientUser,
      productId,
      token: accountSettings.sessionToken,
    }
    const current = await jamsService.getProductSettings(getParams)

    // Build a new placements object WITHOUT:
    //    - the placement itself
    //    - its matching preview__<placementId> entry
    const newPlacements = Object.entries(current?.placements ?? {}).reduce(
      (acc: Record<string, any>, [key, value]) => {
        if (key === placementId || key === `preview__${placementId}`) {
          return acc
        }
        acc[key] = value
        return acc
      },
      {},
    )

    // Push up the trimmed-down placements
    const payload: IApiProductSettings = {
      a5AccountId: accountSettings.inventoryIds,
      placements: newPlacements,
    }
    return await pushChanges(payload)
  }

  const saveSettings = async (newSettings: IPlacements, refreshData = true) => {
    const getProductSettingsParams = {
      accountId: accountSettings.accountId,
      endpoint: appConfig.jamsBackendUrl,
      isClientUser: accountSettings.isClientUser,
      productId: productId,
      token: accountSettings.sessionToken,
    }

    const currentSettings = await jamsService.getProductSettings(
      getProductSettingsParams,
    )

    const payload: IApiProductSettings = {
      a5AccountId: accountSettings.inventoryIds,
      placements: {
        ...(currentSettings?.placements ?? {}),
        ...newSettings,
      },
    }

    if (refreshData) {
      placements.value = {
        ...(currentSettings?.placements ?? {}),
        ...newSettings,
      }
    }

    return await pushChanges(payload, refreshData)
  }

  const pushChanges = async (
    payload: IApiProductSettings,
    refreshData: boolean = true,
  ) => {
    const params = {
      accountId: accountSettings.accountId,
      endpoint: appConfig.jamsBackendUrl,
      isClientUser: accountSettings.isClientUser,
      settings: payload,
      productId: appConfig.productId,
      token: accountSettings.sessionToken,
    }
    const savingResult = await jamsService.setProductSettings(params)

    if (!savingResult) {
      error.value = new Error(`Error saving product settings`)
    } else if (refreshData) {
      updateSettings(payload.placements)
    }

    return savingResult
  }

  const ensurePlacementDefaults = (placements: any) => {
    const result: IPlacements = {}

    Object.keys(placements || {}).forEach(placementId => {
      const originalPlacement = placements[placementId]

      // Create a completely new object with all properties
      result[placementId] = {
        enabled: originalPlacement.enabled ?? true,
        friendlyName:
          originalPlacement.friendlyName ??
          formatPlacementIdToFriendlyName(placementId),
        url: originalPlacement.url ?? '',
        layout: ensureLayoutSettings(originalPlacement.layout),
        numberOfVehicles: originalPlacement.numberOfVehicles ?? 4,
        activeFilters: ensureActiveFilters(originalPlacement.activeFilters),
        activeFilterGroups: ensureActiveFilterGroups(
          originalPlacement.activeFilterGroups,
        ),
        availableFilterGroups: ensureAvailableFilterGroups(
          originalPlacement.availableFilterGroups,
        ),
        vehicleDetailRows: ensureVehicleDetailsRows(
          originalPlacement.vehicleDetailRows,
        ),
        callToAction: ensureCallToAction(originalPlacement.callToAction),
        cardSettings: ensureCardSettings(originalPlacement.cardSettings),
        sliderArrowSettings: ensureSliderArrowSettings(
          originalPlacement.sliderArrowSettings,
        ),
        activeVehicles: ensureActiveVehicles(originalPlacement.activeVehicles),
        sortBy: ensureSortBy(originalPlacement.sortBy),
        sortDirection: ensureSortDirection(originalPlacement.sortDirection),
        secondarySorts: originalPlacement.secondarySorts ?? [],
        // Copy any other properties that might exist
        ...Object.fromEntries(
          Object.entries(originalPlacement || {}).filter(
            ([key]) =>
              ![
                'friendlyName',
                'url',
                'layout',
                'numberOfVehicles',
                'activeFilters',
                'activeFilterGroups',
                'availableFilterGroups',
                'vehicleDetailRows',
                'callToAction',
                'cardSettings',
                'sliderArrowSettings',
                'activeVehicles',
                'sortBy',
                'sortDirection',
              ].includes(key),
          ),
        ),
      }
    })

    return result
  }

  return {
    // State
    productId,
    placements,
    loading,
    error,
    initialized,

    // Actions
    getProductSettings,
    setProductSettings,
    updateSettings,
    saveSettings,
    deletePlacement,
    resetError,
  }
})
